import { KeyStatus } from "../../core/engine/types";
import { IInputManager } from "../../core/engine/types/input-manager";
import { IInputSource } from "./input-source";

/**
 * Unified input manager that combines multiple input sources.
 * This provides a clean abstraction for handling any number of input types.
 * 
 * Priority: Input sources are checked in order. The first enabled source
 * that has a non-NONE state for an action takes priority.
 * 
 * For rotation actions (keyRotate, keyRotateTwice), RELEASED status takes
 * priority over PRESSED to ensure rotations are properly detected.
 */
export default class UnifiedInputManager implements IInputManager {
  private _keyRotate = KeyStatus.NONE;
  private _keyRotateTwice = KeyStatus.NONE;
  private _keyDown = KeyStatus.NONE;
  private _keyRight = KeyStatus.NONE;
  private _keyLeft = KeyStatus.NONE;
  private _keyDrop = KeyStatus.NONE;

  // Getters and setters to track when game clears states
  // When game clears a state (sets to NONE), also clear it in sources to prevent re-triggering
  get keyRotate(): KeyStatus { return this._keyRotate; }
  set keyRotate(value: KeyStatus) {
    const previousValue = this._keyRotate;
    this._keyRotate = value;
    // If game clears a state (was non-NONE, now NONE), also clear it in all sources
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyRotate');
    }
  }

  get keyRotateTwice(): KeyStatus { return this._keyRotateTwice; }
  set keyRotateTwice(value: KeyStatus) {
    const previousValue = this._keyRotateTwice;
    this._keyRotateTwice = value;
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyRotateTwice');
    }
  }

  get keyDown(): KeyStatus { return this._keyDown; }
  set keyDown(value: KeyStatus) {
    const previousValue = this._keyDown;
    this._keyDown = value;
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyDown');
    }
  }

  get keyRight(): KeyStatus { return this._keyRight; }
  set keyRight(value: KeyStatus) {
    const previousValue = this._keyRight;
    this._keyRight = value;
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyRight');
    }
  }

  get keyLeft(): KeyStatus { return this._keyLeft; }
  set keyLeft(value: KeyStatus) {
    const previousValue = this._keyLeft;
    this._keyLeft = value;
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyLeft');
    }
  }

  get keyDrop(): KeyStatus { return this._keyDrop; }
  set keyDrop(value: KeyStatus) {
    const previousValue = this._keyDrop;
    this._keyDrop = value;
    if (previousValue !== KeyStatus.NONE && value === KeyStatus.NONE) {
      this.clearStateInSources('keyDrop');
    }
  }

  private inputSources: IInputSource[] = [];

  /**
   * Add an input source to be managed
   */
  addInputSource(source: IInputSource): void {
    if (!this.inputSources.includes(source)) {
      this.inputSources.push(source);
    }
  }

  /**
   * Remove an input source
   */
  removeInputSource(source: IInputSource): void {
    const index = this.inputSources.indexOf(source);
    if (index >= 0) {
      this.inputSources.splice(index, 1);
    }
  }

  /**
   * Update all input sources and merge their states
   */
  update(): void {
    // Update all input sources
    this.inputSources.forEach(source => {
      if (source.isEnabled()) {
        source.update();
      }
    });

    // Merge states from all enabled sources
    this.mergeInputStates();
  }

  /**
   * Merge input states from all enabled sources.
   * Combines inputs from all sources - if any source has an active state, use it.
   */
  private mergeInputStates(): void {
    // Reset all states (use private fields to avoid triggering setters)
    this._keyRotate = KeyStatus.NONE;
    this._keyRotateTwice = KeyStatus.NONE;
    this._keyDown = KeyStatus.NONE;
    this._keyRight = KeyStatus.NONE;
    this._keyLeft = KeyStatus.NONE;
    this._keyDrop = KeyStatus.NONE;

    // Collect states from all enabled sources
    const enabledSources = this.inputSources.filter(source => source.isEnabled());
    
    if (enabledSources.length === 0) {
      return;
    }

    // For each action, merge states from all sources
    const actions: Array<keyof IInputManager> = [
      'keyRotate',
      'keyRotateTwice',
      'keyDown',
      'keyRight',
      'keyLeft',
      'keyDrop',
    ];

    actions.forEach(action => {
      const isRotateAction = action === 'keyRotate' || action === 'keyRotateTwice';
      
      // For rotation actions, prioritize RELEASED status (rotations trigger on release)
      if (isRotateAction) {
        // Check all sources for RELEASED first (highest priority for rotations)
        for (const source of enabledSources) {
          const state = source.getState();
          const status = state[action];
          if (status === KeyStatus.RELEASED) {
            (this as any)[`_${action}`] = KeyStatus.RELEASED;
            return; // RELEASED takes priority, stop checking
          }
        }
        
        // Then check all sources for PRESSED
        for (const source of enabledSources) {
          const state = source.getState();
          const status = state[action];
          if (status === KeyStatus.PRESSED || (status & KeyStatus.PRESSED)) {
            (this as any)[`_${action}`] = KeyStatus.PRESSED;
            return; // Found PRESSED, stop checking
          }
        }
      } else {
        // For non-rotation actions, merge from all sources
        // Check all sources for PRESSED first (active input takes priority)
        for (const source of enabledSources) {
          const state = source.getState();
          const status = state[action];
          if (status === KeyStatus.PRESSED || (status & KeyStatus.PRESSED)) {
            (this as any)[`_${action}`] = KeyStatus.PRESSED;
            return; // PRESSED takes priority, stop checking
          }
        }
        
        // Then check all sources for RELEASED
        for (const source of enabledSources) {
          const state = source.getState();
          const status = state[action];
          if (status === KeyStatus.RELEASED) {
            (this as any)[`_${action}`] = KeyStatus.RELEASED;
            return; // Found RELEASED, stop checking
          }
        }
      }
    });
  }

  /**
   * Clear a state in all input sources when game consumes it
   */
  private clearStateInSources(action: keyof IInputManager): void {
    // Clear the state in all input sources that implement IInputManager
    this.inputSources.forEach(source => {
      if ('keyRotate' in source) {
        (source as any)[action] = KeyStatus.NONE;
      }
    });
  }

  /**
   * Get all input sources
   */
  getInputSources(): readonly IInputSource[] {
    return [...this.inputSources];
  }
}

